import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const LegalServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const res = await contentAPI.get('legal_services'); setServices(Array.isArray(res.data) ? res.data : []); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try { await contentAPI.update('legal_services', services); toast.success('Legal services saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const update = (idx, field, value) => {
    const copy = [...services];
    copy[idx] = { ...copy[idx], [field]: value };
    setServices(copy);
  };

  const add = () => setServices([...services, { id: `legal-${Date.now()}`, title: '', description: '' }]);
  const remove = (idx) => setServices(services.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Edit legal advisory services displayed on the Legal Advisory page.</p>
        <Button size="sm" onClick={add}><Plus className="w-4 h-4 mr-1" /> Add Service</Button>
      </div>
      {services.map((svc, idx) => (
        <Card key={svc.id || idx}>
          <CardContent className="space-y-3 pt-5">
            <div className="flex gap-3">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                  <Input value={svc.title || ''} onChange={e => update(idx, 'title', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">ID</label>
                  <Input value={svc.id || ''} onChange={e => update(idx, 'id', e.target.value)} />
                </div>
              </div>
              <Button size="sm" variant="destructive" onClick={() => remove(idx)} className="mt-6"><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
              <Textarea rows={2} value={svc.description || ''} onChange={e => update(idx, 'description', e.target.value)} />
            </div>
          </CardContent>
        </Card>
      ))}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-legal">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Legal Services
      </Button>
    </div>
  );
};

export default LegalServicesEditor;
